import { useEffect, useState } from "react";
import { api } from "./api.js";
import Menu, { Chevron, Check } from "./Menu.jsx";
import { useTheme, THEMES, LOOK, useLook } from "./theme.jsx";
import { shortName } from "./company/common.jsx";
import GlobalSearch from "./GlobalSearch.jsx";
import Icon from "./icons.jsx";

// Шапка кабінету: [☰] etax … пошук … аватар · ім'я / компанія ▾.
// У меню користувача — email, перемикання компанії, тема й вихід
const initials = (u) => {
  const n = String(u?.name || "").trim();
  if (n) return n.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return String(u?.email || "?").replace(/@.*/, "").replace(/[^\p{L}\p{N}]/gu, "").slice(0, 2).toUpperCase() || "?";
};

export default function TopBar({ user, route, onLogout, navOpen, onNavToggle }) {
  const [companies, setCompanies] = useState(null);
  const [theme, setTheme] = useTheme();
  const [skin, setSkin] = useLook("skin"), [font, setFont] = useLook("font"), [accent, setAccent] = useLook("accent");
  const companyId = route.page === "company" ? route.id : null;
  // Перелік компаній — для перемикача й пошуку на сторінці компаній; оновлюємо, коли відкрили нову
  useEffect(() => {
    if (companies && (!companyId || companies.some((c) => c.id === companyId))) return;
    api("/api/companies").then((r) => setCompanies(r.companies)).catch(() => setCompanies([]));
  }, [companyId, companies]);
  const current = companies?.find((c) => c.id === companyId);
  const section = route.section || "overview";
  const who = user.name || user.email.replace(/@.*/, "");

  return (
    <header className="appbar">
      <div className="appbar-row">
        {companyId && (
          <button type="button" className="icon-btn nav-toggle" aria-label={navOpen ? "Закрити меню розділів" : "Меню розділів"}
            aria-expanded={navOpen} aria-controls="cab-nav" onClick={onNavToggle}>
            <Icon name={navOpen ? "close" : "menu"} size={20} />
          </button>
        )}
        <a href="#/" className="brand" aria-label="eCabinet — усі компанії"><img className="brand-logo" src="/logo.png" width="28" height="28" alt="" />eCabinet</a>
        <GlobalSearch companyId={companyId} companies={companies} />
        <Menu label={`Меню користувача ${user.email}`} className="user-menu"
          button={<>
            <span className="avatar" aria-hidden="true">{initials(user)}</span>
            <span className="user-id">
              <span className="user-name">{who}</span>
              <span className="user-co">{current ? shortName(current.name) || current.edrpou : companyId ? "…" : "Усі компанії"}</span>
            </span>
            <Chevron />
          </>}>
          <div className="menu-head">
            <span className="avatar avatar-lg" aria-hidden="true">{initials(user)}</span>
            <span className="menu-text">{user.name && <b>{user.name}</b>}<span className="muted small menu-email">{user.email}</span></span>
          </div>
          {companies?.length > 0 && (
            <>
              <hr className="menu-sep" />
              <p className="menu-caption" id="co-caption">Компанія</p>
              <div role="group" aria-labelledby="co-caption">
                {companies.map((c) => (
                  <a key={c.id} role="menuitemradio" aria-checked={c.id === companyId} href={`#/c/${c.id}/${companyId ? section : "overview"}`} className="menu-item">
                    <span className="menu-check">{c.id === companyId && <Check />}</span>
                    <span className="menu-text"><span>{shortName(c.name) || c.edrpou}</span><span className="muted small">ЄДРПОУ {c.edrpou}</span></span>
                  </a>
                ))}
                <a role="menuitem" href="#/" className="menu-item"><span className="menu-check" />Усі компанії</a>
              </div>
            </>
          )}
          {user.activated && <a role="menuitem" href="#/add" className="menu-item"><span className="menu-check" aria-hidden="true">+</span>Додати компанію</a>}
          <hr className="menu-sep" />
          {/* Кабінет користувача: профіль, компанії, тариф і оплата; до активації — профіль і активація */}
          <a role="menuitem" href="#/account/profile" className="menu-item"><span className="menu-check" aria-hidden="true" />Профіль</a>
          {user.activated ? <>
            <a role="menuitem" href="#/account/companies" className="menu-item"><span className="menu-check" aria-hidden="true" />Мої компанії</a>
            <a role="menuitem" href="#/account/billing" className="menu-item"><span className="menu-check" aria-hidden="true" />Тариф і оплата</a>
            {user.activatedUntil && <a role="menuitem" href="#/activate" className="menu-item"><span className="menu-check" aria-hidden="true" />
              <span className="menu-text"><span>Продовжити доступ</span><span className="muted small">до {user.activatedUntil.split("-").reverse().join(".")}</span></span></a>}
          </> : <a role="menuitem" href="#/activate" className="menu-item"><span className="menu-check" aria-hidden="true" />Активувати акаунт</a>}
          {user.isAdmin && <a role="menuitem" href="#/admin" className="menu-item"><span className="menu-check" aria-hidden="true" />Адмінка</a>}
          <hr className="menu-sep" />
          <p className="menu-caption" id="theme-caption">Тема</p>
          <div role="group" aria-labelledby="theme-caption">
            {THEMES.map((t) => (
              <button key={t.key} type="button" role="menuitemradio" aria-checked={theme === t.key} className="menu-item" onClick={() => setTheme(t.key)}>
                <span className="menu-check">{theme === t.key && <Check />}</span>{t.label.replace(" тема", "")}
              </button>
            ))}
          </div>
          <LookGroup name="skin" caption="Вигляд" value={skin} onPick={setSkin} />
          {skin === "minimal" && <>
            <LookGroup name="font" caption="Шрифт" value={font} onPick={setFont} />
            <LookGroup name="accent" caption="Акцент" value={accent} onPick={setAccent} />
          </>}
          <hr className="menu-sep" />
          <button type="button" role="menuitem" className="menu-item menu-danger" onClick={onLogout}><span className="menu-check" />Вийти</button>
        </Menu>
      </div>
    </header>
  );
}

// Група вибору параметра вигляду в меню користувача (як вибір теми)
function LookGroup({ name, caption, value, onPick }) {
  const id = `look-${name}-caption`;
  return (
    <>
      <p className="menu-caption" id={id}>{caption}</p>
      <div role="group" aria-labelledby={id}>
        {LOOK[name].options.map((o) => (
          <button key={o.key} type="button" role="menuitemradio" aria-checked={value === o.key} className="menu-item" onClick={() => onPick(o.key)}>
            <span className="menu-check">{value === o.key && <Check />}</span>{o.label}
          </button>
        ))}
      </div>
    </>
  );
}
